import { addMonths, format, subDays } from 'date-fns';

export enum Fields {
  fundingAmount = 'fundingAmount',
  frameworkAgreement = 'frameworkAgreement',
  leasingCategory = 'leasingCategory',
  firstDueDate = 'firstDueDate',
  firstDueMonth = 'firstDueMonth',
  firstDueYear = 'firstDueYear',

  calculationPath = 'calculationPath',

  residualValue = 'residualValue',
  leasingPeriod = 'leasingPeriod',
  leasingPayment = 'leasingPayment',
  paymentFrequency = 'paymentFrequency',
  fixedFirstPayment = 'fixedFirstPayment',

  description = 'description',
  extendedDescription = 'extendedDescription',
  municipalOrRegionalPurpose = 'municipalOrRegionalPurpose',

  expectedKmPerYear = 'expectedKmPerYear',
  carAttachment = 'carAttachment',

  lessee = 'lessee',
  eanNumber = 'eanNumber',
  newLessee = 'newLessee',
  newEanNumber = 'newEanNumber',

  deliveryApprover = 'deliveryApprover',
  newDeliveryApprover = 'newDeliveryApprover',
  contactPerson = 'contactPerson',
  newContactPerson = 'newContactPerson',
  authorizedSignatory = 'authorizedSignatory',
  newAuthorizedSignatory = 'newAuthorizedSignatory',

  confirmMayor = 'confirmMayor',
  confirmMunicipalDirector = 'confirmMunicipalDirector',
  contractResponsible = 'contractResponsible',
  newContractResponsible = 'newContractResponsible',

  createMultipleIdenticalAgreements = 'createMultipleIdenticalAgreements',
  numberOfIdenticalAgreements = 'numberOfIdenticalAgreements',
  remarks = 'remarks',
  attachment = 'attachment',
}

export const ENABLE_OWNERSHIP_CONFIRMATION: boolean = false;

export type SelectOption = {
  value: string;
  label: string;
};

export type NewContractResponsibleValues = {
  name: string;
  position?: string;
  phoneNumber: string;
  email: string;
};

export type NewLesseeValues = {
  name: string;
  address: string;
  postalCode: string;
  city: string;
};

export type NewEanNumberValues = {
  eanNumber: string;
};

export type NewAgreementFormValues = {
  [Fields.fundingAmount]: string;
  [Fields.frameworkAgreement]: string;
  [Fields.leasingCategory]: string;
  [Fields.firstDueDate]: string;
  [Fields.firstDueMonth]: string;
  [Fields.firstDueYear]: string;

  [Fields.calculationPath]: string;

  [Fields.residualValue]: string;
  [Fields.leasingPeriod]: string;
  [Fields.leasingPayment]: string;
  [Fields.paymentFrequency]: string;
  [Fields.fixedFirstPayment]: string;

  [Fields.description]: string;
  [Fields.extendedDescription]: string;
  [Fields.municipalOrRegionalPurpose]: string;

  [Fields.expectedKmPerYear]: string;
  [Fields.carAttachment]: File[];

  [Fields.lessee]: string;
  [Fields.eanNumber]: string;
  [Fields.newLessee]: NewLesseeValues | null;
  [Fields.newEanNumber]: NewEanNumberValues | null;

  [Fields.deliveryApprover]: string;
  [Fields.newDeliveryApprover]: NewContractResponsibleValues | null;
  [Fields.contactPerson]: string;
  [Fields.newContactPerson]: NewContractResponsibleValues | null;
  [Fields.authorizedSignatory]: string;
  [Fields.newAuthorizedSignatory]:
    | NewContractResponsibleValues
    | NewContractResponsibleValues[]
    | null;

  [Fields.confirmMayor]: boolean;
  [Fields.confirmMunicipalDirector]: boolean;
  [Fields.contractResponsible]: string;
  [Fields.newContractResponsible]: NewContractResponsibleValues | null;

  [Fields.createMultipleIdenticalAgreements]: boolean;
  [Fields.numberOfIdenticalAgreements]: string;
  [Fields.remarks]: string;
  [Fields.attachment]: File[];
};

export const defaultNewAgreementFormValues: NewAgreementFormValues = {
  [Fields.fundingAmount]: '',
  [Fields.frameworkAgreement]: '',
  [Fields.leasingCategory]: '',
  [Fields.firstDueDate]: '',
  [Fields.firstDueMonth]: '',
  [Fields.firstDueYear]: '',

  [Fields.calculationPath]: '',

  [Fields.residualValue]: '',
  [Fields.leasingPeriod]: '',
  [Fields.leasingPayment]: '',
  [Fields.paymentFrequency]: '',
  [Fields.fixedFirstPayment]: '',

  [Fields.description]: '',
  [Fields.extendedDescription]: '',
  [Fields.municipalOrRegionalPurpose]: '',

  [Fields.expectedKmPerYear]: '',
  [Fields.carAttachment]: [],

  [Fields.lessee]: '',
  [Fields.eanNumber]: '',
  [Fields.newLessee]: null,
  [Fields.newEanNumber]: null,

  [Fields.deliveryApprover]: '',
  [Fields.newDeliveryApprover]: null,
  [Fields.contactPerson]: '',
  [Fields.newContactPerson]: null,
  [Fields.authorizedSignatory]: '',
  [Fields.newAuthorizedSignatory]: null,

  [Fields.confirmMayor]: false,
  [Fields.confirmMunicipalDirector]: false,
  [Fields.contractResponsible]: '',
  [Fields.newContractResponsible]: null,

  [Fields.createMultipleIdenticalAgreements]: false,
  [Fields.numberOfIdenticalAgreements]: '',
  [Fields.remarks]: '',
  [Fields.attachment]: [],
};

export const REQUIRED_FOR_SHOW_CALC: Array<keyof NewAgreementFormValues> = [
  Fields.frameworkAgreement,
  Fields.leasingCategory,
  Fields.firstDueDate,
];

export const REQUIRED_FOR_CALCULATION: Array<keyof NewAgreementFormValues> = [
  Fields.fundingAmount,
  Fields.residualValue,
  Fields.leasingPeriod,
  Fields.leasingPayment,
  Fields.paymentFrequency,
];

export const hasValueProp = (
  value: unknown,
): value is { value: string; label?: string } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    typeof (value as { value?: unknown }).value === 'string'
  );
};

export const getSelectValue = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (hasValueProp(value)) return value.value;
  return '';
};

export const LEASING_CATEGORY_PERSONBIL = 'PERSONBIL';
export const LEASING_CATEGORY_PERSONBIL_LABEL = 'Personbil';
export const LEASING_CATEGORY_VAREBIL = 'VAREBIL';

export const CAR_LEASING_CATEGORIES = [
  LEASING_CATEGORY_PERSONBIL,
  LEASING_CATEGORY_VAREBIL,
] as const;

export const isCarLeasingCategory = (value: unknown): boolean => {
  const selectedValue = getSelectValue(value).trim().toUpperCase();

  return CAR_LEASING_CATEGORIES.includes(
    selectedValue as (typeof CAR_LEASING_CATEGORIES)[number],
  );
};

export const isPassengerCarCategory = isCarLeasingCategory;

export const parseNumber = (
  value: string | null | undefined,
): number | null => {
  if (!value?.trim()) return null;

  const normalized = value.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);

  return Number.isNaN(parsed) ? null : parsed;
};

export const pickBasisSnapshot = (
  values: NewAgreementFormValues,
): Pick<
  NewAgreementFormValues,
  Fields.frameworkAgreement | Fields.leasingCategory | Fields.firstDueDate
> => ({
  [Fields.frameworkAgreement]: values[Fields.frameworkAgreement],
  [Fields.leasingCategory]: values[Fields.leasingCategory],
  [Fields.firstDueDate]: normalizeDateValue(values[Fields.firstDueDate]),
});

export const isSameBasisSnapshot = (
  a: ReturnType<typeof pickBasisSnapshot> | null,
  b: ReturnType<typeof pickBasisSnapshot> | null,
): boolean => {
  if (!a || !b) return false;

  return (
    getSelectValue(a[Fields.frameworkAgreement]) ===
      getSelectValue(b[Fields.frameworkAgreement]) &&
    getSelectValue(a[Fields.leasingCategory]) ===
      getSelectValue(b[Fields.leasingCategory]) &&
    normalizeDateValue(a[Fields.firstDueDate]) ===
      normalizeDateValue(b[Fields.firstDueDate])
  );
};

export const parseDateString = (
  value: string | Date | null | undefined,
): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmedValue = String(value).trim();

  if (!trimmedValue) return null;

  const date = new Date(trimmedValue);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const normalizeDateValue = (
  value: string | Date | null | undefined,
): string => {
  if (!value) return '';

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }

  return String(value).trim();
};

export const calculateExpiryDate = (
  firstDueDate: string | Date | null | undefined,
  leasingPeriod: string,
): string => {
  const date = parseDateString(firstDueDate);
  const months = parseNumber(leasingPeriod);

  if (!date || months === null) {
    return '-';
  }

  return format(subDays(addMonths(date, months), 1), 'dd/MM/yyyy');
};

export type ExistingOrCreate<TCreate> =
  | {
      id: string | number;
      create?: never;
    }
  | {
      id?: never;
      create: TCreate;
    };

export type AuthorizedSignatoryCreateValues = {
  name: string;
  title?: string;
  phoneNumber: string;
  email: string;
};

export type AuthorizedSignatoryPayload =
  ExistingOrCreate<AuthorizedSignatoryCreateValues>;

export type NewAgreementApplicationPayload = {
  [Fields.fundingAmount]: string;
  [Fields.frameworkAgreement]: string | null;
  [Fields.leasingCategory]: string;
  [Fields.firstDueDate]: string;
  [Fields.firstDueMonth]: string;
  [Fields.firstDueYear]: string;

  [Fields.calculationPath]: string;

  [Fields.residualValue]: string;
  [Fields.leasingPeriod]: string;
  [Fields.leasingPayment]: string;
  [Fields.paymentFrequency]: string;
  [Fields.fixedFirstPayment]: string;

  [Fields.description]: string;
  [Fields.extendedDescription]: string;
  [Fields.municipalOrRegionalPurpose]: string;

  [Fields.expectedKmPerYear]: string;
  [Fields.carAttachment]: File[];

  [Fields.lessee]: ExistingOrCreate<NewLesseeValues> | null;
  [Fields.eanNumber]: ExistingOrCreate<NewEanNumberValues> | null;
  [Fields.deliveryApprover]: ExistingOrCreate<NewContractResponsibleValues> | null;
  [Fields.contactPerson]: ExistingOrCreate<NewContractResponsibleValues> | null;
  authorizedSignatories: AuthorizedSignatoryPayload[];

  [Fields.confirmMayor]: boolean;
  [Fields.confirmMunicipalDirector]: boolean;
  [Fields.contractResponsible]: ExistingOrCreate<NewContractResponsibleValues> | null;

  [Fields.createMultipleIdenticalAgreements]: boolean;
  [Fields.numberOfIdenticalAgreements]: string;
  [Fields.remarks]: string;
  [Fields.attachment]: File[];
};

const trimOrNull = (value: unknown): string | null => {
  const selectedValue = getSelectValue(value).trim();

  return selectedValue || null;
};

const toExistingOrCreate = <TCreate>(
  selectedValue: unknown,
  createdValue: TCreate | null | undefined,
): ExistingOrCreate<TCreate> | null => {
  if (createdValue) {
    return {
      create: createdValue,
    };
  }

  const id = trimOrNull(selectedValue);

  return id ? { id } : null;
};

const toPayloadId = (id: string): string | number => {
  return /^\d+$/.test(id) ? Number(id) : id;
};

const toAuthorizedSignatoryCreateValues = (
  value: NewContractResponsibleValues,
): AuthorizedSignatoryCreateValues => ({
  name: value.name,
  title: value.position,
  phoneNumber: value.phoneNumber,
  email: value.email,
});

const getAuthorizedSignatoryCreateKey = (
  value: NewContractResponsibleValues,
): string => `${value.name}, ${value.email}`.trim().toLowerCase();

const toAuthorizedSignatoryPayload = (
  selectedValue: unknown,
  createdValue:
    | NewContractResponsibleValues
    | NewContractResponsibleValues[]
    | null
    | undefined,
): AuthorizedSignatoryPayload[] => {
  const selectedIds = getSelectValue(selectedValue)
    .split('|')
    .map((value) => value.trim())
    .filter(Boolean);

  const createdValues = Array.isArray(createdValue)
    ? createdValue
    : createdValue
      ? [createdValue]
      : [];

  const createdByLabel = new Map(
    createdValues.map((value) => [
      getAuthorizedSignatoryCreateKey(value),
      value,
    ]),
  );

  const matchedCreatedKeys = new Set<string>();

  const selectedPayloads = selectedIds.map((id) => {
    const createdItem = createdByLabel.get(id.toLowerCase());

    if (createdItem) {
      matchedCreatedKeys.add(getAuthorizedSignatoryCreateKey(createdItem));

      return { create: toAuthorizedSignatoryCreateValues(createdItem) };
    }

    return { id: toPayloadId(id) };
  });

  const unselectedCreatedPayloads = createdValues
    .filter(
      (value) =>
        !matchedCreatedKeys.has(getAuthorizedSignatoryCreateKey(value)),
    )
    .map((value) => ({
      create: toAuthorizedSignatoryCreateValues(value),
    }));

  return [...selectedPayloads, ...unselectedCreatedPayloads];
};

export const toNewAgreementApplicationPayload = (
  values: NewAgreementFormValues,
): NewAgreementApplicationPayload => ({
  [Fields.fundingAmount]: values[Fields.fundingAmount],
  [Fields.frameworkAgreement]: trimOrNull(values[Fields.frameworkAgreement]),
  [Fields.leasingCategory]: values[Fields.leasingCategory],
  [Fields.firstDueDate]: values[Fields.firstDueDate],
  [Fields.firstDueMonth]: values[Fields.firstDueMonth],
  [Fields.firstDueYear]: values[Fields.firstDueYear],

  [Fields.calculationPath]: values[Fields.calculationPath],

  [Fields.residualValue]: values[Fields.residualValue],
  [Fields.leasingPeriod]: values[Fields.leasingPeriod],
  [Fields.leasingPayment]: values[Fields.leasingPayment],
  [Fields.paymentFrequency]: values[Fields.paymentFrequency],
  [Fields.fixedFirstPayment]: values[Fields.fixedFirstPayment],

  [Fields.description]: values[Fields.description],
  [Fields.extendedDescription]: values[Fields.extendedDescription],
  [Fields.municipalOrRegionalPurpose]:
    values[Fields.municipalOrRegionalPurpose],

  [Fields.expectedKmPerYear]: values[Fields.expectedKmPerYear],
  [Fields.carAttachment]: values[Fields.carAttachment],

  [Fields.lessee]: toExistingOrCreate(
    values[Fields.lessee],
    values[Fields.newLessee],
  ),
  [Fields.eanNumber]: toExistingOrCreate(
    values[Fields.eanNumber],
    values[Fields.newEanNumber],
  ),
  [Fields.deliveryApprover]: toExistingOrCreate(
    values[Fields.deliveryApprover],
    values[Fields.newDeliveryApprover],
  ),
  [Fields.contactPerson]: toExistingOrCreate(
    values[Fields.contactPerson],
    values[Fields.newContactPerson],
  ),
  authorizedSignatories: toAuthorizedSignatoryPayload(
    values[Fields.authorizedSignatory],
    values[Fields.newAuthorizedSignatory],
  ),

  [Fields.confirmMayor]: values[Fields.confirmMayor],
  [Fields.confirmMunicipalDirector]: values[Fields.confirmMunicipalDirector],
  [Fields.contractResponsible]: toExistingOrCreate(
    values[Fields.contractResponsible],
    values[Fields.newContractResponsible],
  ),

  [Fields.createMultipleIdenticalAgreements]:
    values[Fields.createMultipleIdenticalAgreements],
  [Fields.numberOfIdenticalAgreements]:
    values[Fields.numberOfIdenticalAgreements],
  [Fields.remarks]: values[Fields.remarks],
  [Fields.attachment]: values[Fields.attachment],
});
